import { computed, useHead, useI18n, useRoute, useRuntimeConfig } from '#imports'

/**
 * Build Cloudinary URL with transformations for OG/Twitter images
 */
function buildCloudinaryOgImageUrl(publicId: string, cloudBase: string, width: number = 1200, height: number = 630): string {
  // If it's already a full Cloudinary URL, extract the public ID
  if (publicId.startsWith('http')) {
    const idx = publicId.indexOf('/image/upload/')
    if (idx !== -1) {
      // Extract public ID, handling existing transformations
      const afterUpload = publicId.slice(idx + '/image/upload/'.length)
      // Remove any existing transformations (everything before the last segment)
      const segments = afterUpload.split('/')
      publicId = segments[segments.length - 1] || afterUpload
    } else if (publicId.includes('res.cloudinary.com')) {
      // Handle URLs with transformations already
      const parts = publicId.split('/image/upload/')
      if (parts.length > 1) {
        // Extract just the public ID (remove any existing transformations)
        const afterUpload = parts[1]!
        const segments = afterUpload.split('/')
        publicId = segments[segments.length - 1] || afterUpload
      }
    }
  }
  
  // Build URL with transformations for OG image
  // w_1200,h_630,c_fill,g_auto,q_auto,f_auto
  // - w_1200: width 1200px
  // - h_630: height 630px
  // - c_fill: fill crop mode (maintains aspect ratio, fills dimensions)
  // - g_auto: auto gravity (smart cropping - focuses on faces/important areas)
  // - q_auto: auto quality (optimizes for file size vs quality)
  // - f_auto: auto format (webp preferred, falls back to original)
  const transformations = `w_${width},h_${height},c_fill,g_auto,q_auto,f_auto`
  return `${cloudBase}/${transformations}/${publicId}`
}

export interface SeoOptions {
  title: string
  description: string
  keywords?: string
  image?: string
  imageAlt?: string
  type?: 'website' | 'article' | 'product' | 'profile'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  noindex?: boolean
  nofollow?: boolean
  canonicalUrl?: string
  structuredData?: Record<string, any> | Array<Record<string, any>>
}

const BASE_URL = 'https://ommi.shop'
const DEFAULT_IMAGE = '/ommi-shop-og-image.png'
const DEFAULT_IMAGE_ALT = 'ommi.shop - Discover rarities through awesome human mistakes'

/**
 * Get the current locale's ISO code for Open Graph
 */
function getOgLocale(locale: string): string {
  const localeMap: Record<string, string> = {
    en: 'en_US',
    de: 'de_DE',
    fr: 'fr_FR',
    it: 'it_IT',
    es: 'es_ES',
    nl: 'nl_NL',
    ja: 'ja_JP',
    zh: 'zh_CN',
    ko: 'ko_KR',
    sv: 'sv_SE'
  }
  return localeMap[locale] || 'en_US'
}

/**
 * Normalize URL by removing trailing slash except for root path
 * Works with both full URLs and relative paths
 */
function normalizeUrl(url: string): string {
  // Extract path from full URL if needed
  let path = url
  let baseUrl = ''
  
  if (url.startsWith('http')) {
    try {
      const urlObj = new URL(url)
      baseUrl = `${urlObj.protocol}//${urlObj.host}`
      path = urlObj.pathname + urlObj.search + urlObj.hash
    } catch {
      // If URL parsing fails, treat as path
      path = url
    }
  }
  
  // Normalize trailing slash: remove trailing slash except for root path
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1)
  }
  
  return baseUrl ? `${baseUrl}${path}` : path
}

/**
 * Build canonical URL
 * Handles locale prefixes correctly - strips existing locale prefix if present
 * Normalizes trailing slashes (removes trailing slash except for root path)
 */
function buildCanonicalUrl(path: string, locale: string, defaultLocale: string): string {
  let cleanPath = path.startsWith('/') ? path : `/${path}`
  
  // Strip locale prefix if it matches the current locale (e.g., /it/about -> /about when locale is 'it')
  // Only strip if it's the exact locale we're using, to avoid issues with paths that start with locale-like segments
  const exactLocalePrefix = `/${locale}/`
  const exactLocaleOnly = `/${locale}`
  
  if (cleanPath.startsWith(exactLocalePrefix)) {
    // Path starts with /{locale}/, remove it but keep the leading /
    cleanPath = '/' + cleanPath.slice(exactLocalePrefix.length)
  } else if (cleanPath === exactLocaleOnly) {
    // Path is exactly /{locale}, convert to /
    cleanPath = '/'
  }
  
  // Normalize trailing slash: remove trailing slash except for root path
  // This ensures consistent canonical URLs and prevents duplicate content issues
  if (cleanPath !== '/' && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1)
  }
  
  const isDefaultLocale = locale === defaultLocale
  const localePrefix = isDefaultLocale ? '' : `/${locale}`
  return normalizeUrl(`${BASE_URL}${localePrefix}${cleanPath}`)
}

/**
 * Build alternate language links (hreflang)
 * Handles locale prefixes correctly - strips existing locale prefix if present
 */
function buildAlternateLinks(
  path: string,
  currentLocale: string,
  supportedLocales: string[],
  defaultLocale: string
): Array<{ rel: string; hreflang: string; href: string }> {
  let cleanPath = path.startsWith('/') ? path : `/${path}`
  
  // Strip locale prefix if it matches any supported locale
  // Check each supported locale to find and remove the prefix
  for (const loc of supportedLocales) {
    const exactLocalePrefix = `/${loc}/`
    const exactLocaleOnly = `/${loc}`
    
    if (cleanPath.startsWith(exactLocalePrefix)) {
      cleanPath = '/' + cleanPath.slice(exactLocalePrefix.length) // Keep the leading /
      break
    } else if (cleanPath === exactLocaleOnly) {
      cleanPath = '/'
      break
    }
  }
  
  // Normalize trailing slash: remove trailing slash except for root path
  if (cleanPath !== '/' && cleanPath.endsWith('/')) {
    cleanPath = cleanPath.slice(0, -1)
  }
  
  const links: Array<{ rel: string; hreflang: string; href: string }> = []

  // Add x-default pointing to default locale
  const defaultPath = defaultLocale === 'en' ? cleanPath : `/${defaultLocale}${cleanPath}`
  links.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${BASE_URL}${defaultPath}`
  })

  // Add all locale variants
  for (const locale of supportedLocales) {
    const isDefault = locale === defaultLocale
    const localePath = isDefault ? cleanPath : `/${locale}${cleanPath}`
    links.push({
      rel: 'alternate',
      hreflang: locale,
      href: `${BASE_URL}${localePath}`
    })
  }

  return links
}

/**
 * Build Organization structured data
 */
function buildOrganizationSchema(): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ommi.shop',
    url: BASE_URL,
    logo: `${BASE_URL}/ommi-shop-og-image.png`,
    description: 'Discover unique pieces from talented artisans and creators. Embrace imperfection and find rarities that tell a story.',
    sameAs: [
      'https://instagram.com/ommiommi.shop'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'hello@ommi.shop'
    }
  }
}

/**
 * Build WebSite structured data
 */
function buildWebSiteSchema(): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ommi.shop',
    url: BASE_URL,
    description: 'Discover unique pieces from talented artisans and creators. Embrace imperfection and find rarities that tell a story.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/humans?query={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }
}

/**
 * Build BreadcrumbList structured data
 */
function buildBreadcrumbSchema(path: string, locale: string, defaultLocale: string): Record<string, any> {
  // Remove locale prefix from path if present (e.g., /it/about -> /about)
  let cleanPath = path
  const exactLocalePrefix = `/${locale}/`
  const exactLocaleOnly = `/${locale}`
  
  if (cleanPath.startsWith(exactLocalePrefix)) {
    cleanPath = '/' + cleanPath.slice(exactLocalePrefix.length) // Keep the leading /
  } else if (cleanPath === exactLocaleOnly) {
    cleanPath = '/'
  }
  const segments = cleanPath.split('/').filter(Boolean)
  const items: Array<{ '@type': string; position: number; name: string; item: string }> = []

  // Home
  const homeUrl = locale === defaultLocale ? BASE_URL : `${BASE_URL}/${locale}`
  items.push({
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: homeUrl
  })

  // Build breadcrumbs from path segments
  let currentPath = ''
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    const isDefault = locale === defaultLocale
    const fullPath = isDefault ? currentPath : `/${locale}${currentPath}`
    
    // Convert segment to readable name (handle numeric IDs and slugs)
    let name = segment
    if (/^\d+$/.test(segment)) {
      // Numeric segment (like collection ID)
      name = `#${segment}`
    } else {
      // Text segment - convert kebab-case to Title Case
      name = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name,
      item: `${BASE_URL}${fullPath}`
    })
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  }
}

/**
 * Build Person/Artist structured data
 */
export function buildPersonSchema(artist: {
  name: string
  bio?: string
  tagline?: string
  photo?: string
  links?: Array<{ url: string; label: string }>
  craft?: string
  slug?: string
}): Record<string, any> {
  const artistUrl = artist.slug 
    ? `${BASE_URL}/of/${artist.slug}`
    : `${BASE_URL}/of/${artist.name.toLowerCase().replace(/\s+/g, '-')}`
  
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: artist.name,
    description: artist.bio || artist.tagline || `Artist profile for ${artist.name} on ommi.shop`,
    jobTitle: artist.craft || 'Artist',
    url: artistUrl
  }

  if (artist.photo) {
    schema.image = {
      '@type': 'ImageObject',
      url: artist.photo.startsWith('http') ? artist.photo : `${BASE_URL}${artist.photo}`,
      caption: `${artist.name} - Artist on ommi.shop`
    }
  }

  if (artist.links && artist.links.length > 0) {
    schema.sameAs = artist.links.map(link => link.url)
  }

  return schema
}

/**
 * Build Product structured data
 */
export function buildProductSchema(product: {
  name: string
  description?: string
  image?: string
  price?: string | number
  currency?: string
  availability?: string
  brand?: string
}): Record<string, any> {
  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.name
  }

  const productImage = Array.isArray(product.images) && product.images.length > 0 
    ? product.images[0] 
    : product.image
  if (productImage) {
    schema.image = productImage.startsWith('http') ? productImage : `${BASE_URL}${productImage}`
  }

  if (product.price) {
    schema.offers = {
      '@type': 'Offer',
      price: typeof product.price === 'string' ? parseFloat(product.price.replace(/[^0-9.]/g, '')) : product.price,
      priceCurrency: product.currency || 'USD',
      availability: product.availability ? `https://schema.org/${product.availability}` : 'https://schema.org/InStock',
      url: `${BASE_URL}/product/${product.name.toLowerCase().replace(/\s+/g, '-')}`
    }
  }

  if (product.brand) {
    schema.brand = {
      '@type': 'Brand',
      name: product.brand
    }
  }

  return schema
}

/**
 * Build CollectionPage structured data
 */
export function buildCollectionPageSchema(collection: {
  name: string
  description?: string
  image?: string
}): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: collection.name,
    description: collection.description || `Browse ${collection.name} collection on ommi.shop`,
    image: collection.image ? (collection.image.startsWith('http') ? collection.image : `${BASE_URL}${collection.image}`) : undefined
  }
}

/**
 * Build FAQPage structured data
 */
export function buildFaqPageSchema(faqs: Array<{
  question: string
  answer: string
}>): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/<[^>]*>/g, '') // Strip HTML tags for text content
      }
    }))
  }
}

/**
 * Build ItemList structured data for product collections
 */
export function buildItemListSchema(items: Array<{
  name: string
  description?: string
  image?: string
  url?: string
}>): Record<string, any> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        description: item.description,
        image: item.image ? (item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`) : undefined,
        url: item.url || undefined
      }
    }))
  }
}

/**
 * Main SEO composable
 */
export function useSeo(options: SeoOptions) {
  const { t, locale, locales } = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()
  const defaultLocale = 'en'
  const supportedLocales = ['en', 'de', 'fr', 'it', 'es', 'nl', 'ja', 'zh', 'ko', 'sv']

  const brand = computed(() => t('common.brand') || 'ommi.shop')
  
  // Get locale ISO code for html lang attribute
  // Use i18n locales configuration directly to avoid duplication
  const localeIsoMap = computed(() => {
    const map: Record<string, string> = {}
    const localeList = locales.value as Array<{ code: string; iso?: string }>
    if (localeList) {
      localeList.forEach((loc) => {
        map[loc.code] = loc.iso || loc.code
      })
    }
    return map
  })
  // Normalize path: remove trailing slash except for root path
  // This ensures consistent paths during prerender (when files are in directories)
  const currentPath = computed(() => {
    const path = route.path
    // Remove trailing slash except for root path
    if (path !== '/' && path.endsWith('/')) {
      return path.slice(0, -1)
    }
    return path
  })
  const currentLocale = computed(() => locale.value || defaultLocale)
  const ogLocale = computed(() => getOgLocale(currentLocale.value))

  // Build canonical URL using custom implementation
  // This ensures canonical URLs are correct at build time without warnings
  const canonicalUrl = computed(() => {
    if (options.canonicalUrl) {
      const url = options.canonicalUrl.startsWith('http') 
        ? options.canonicalUrl 
        : `${BASE_URL}${options.canonicalUrl}`
      return normalizeUrl(url)
    }
    return buildCanonicalUrl(currentPath.value, currentLocale.value, defaultLocale)
  })

  // Build image URL with Cloudinary transformations for OG/Twitter
  const imageUrl = computed(() => {
    if (options.image) {
      const config = useRuntimeConfig()
      const cloudBase: string = (config.public as any)?.cloudinaryBaseURL || 'https://res.cloudinary.com/ommi-shop/image/upload'
      
      // Check if it's a Cloudinary public ID or URL
      const isCloudinaryUrl = options.image.includes('cloudinary.com')
      const isCloudinaryPublicId = !options.image.startsWith('http') && 
                                    !options.image.startsWith('/') && 
                                    !options.image.includes('://')
      
      // If it's a Cloudinary URL or public ID, use Cloudinary transformations
      if (isCloudinaryUrl || isCloudinaryPublicId) {
        // It's a Cloudinary public ID or URL - apply OG image transformations
        return buildCloudinaryOgImageUrl(options.image, cloudBase)
      }
      
      // If it's already a full HTTP URL (non-Cloudinary), use as-is
      if (options.image.startsWith('http')) {
        return options.image
      }
      
      // If it's a local path, make it absolute
      return `${BASE_URL}${options.image}`
    }
    return `${BASE_URL}${DEFAULT_IMAGE}`
  })

  const imageAlt = computed(() => options.imageAlt || DEFAULT_IMAGE_ALT)

  // Build meta tags
  const metaTags = computed(() => {
    const robots = []
    if (options.noindex) robots.push('noindex')
    if (options.nofollow) robots.push('nofollow')
    if (robots.length === 0) robots.push('index', 'follow')

    const tags: Array<Record<string, string>> = [
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'description', content: options.description },
      { name: 'robots', content: robots.join(', ') },
      { name: 'theme-color', content: '#000000' },
      { name: 'format-detection', content: 'telephone=no' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'apple-mobile-web-app-title', content: brand.value },
      { name: 'application-name', content: brand.value },

      // Open Graph
      { property: 'og:type', content: options.type || 'website' },
      { property: 'og:site_name', content: brand.value },
      { property: 'og:title', content: options.title },
      { property: 'og:description', content: options.description },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:image', content: imageUrl.value },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: imageAlt.value },
      { property: 'og:locale', content: ogLocale.value },

      // Twitter Card (minimal - no account specified)
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: options.title },
      { name: 'twitter:description', content: options.description },
      { name: 'twitter:image', content: imageUrl.value },
      { name: 'twitter:image:alt', content: imageAlt.value }
    ]

    // Add keywords if provided
    if (options.keywords) {
      tags.push({ name: 'keywords', content: options.keywords })
    }

    // Add author if provided
    if (options.author) {
      tags.push({ name: 'author', content: options.author })
    }

    // Add article meta if type is article
    if (options.type === 'article') {
      if (options.publishedTime) {
        tags.push({ property: 'article:published_time', content: options.publishedTime })
      }
      if (options.modifiedTime) {
        tags.push({ property: 'article:modified_time', content: options.modifiedTime })
      }
      if (options.author) {
        tags.push({ property: 'article:author', content: options.author })
      }
    }

    return tags
  })

  // Build link tags - merge i18n's links with our custom ones
  const linkTags = computed(() => {
    const links: Array<Record<string, string>> = [
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/site.webmanifest' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' }
    ]

    // Add canonical URL using custom implementation
    // This ensures canonical URLs are correct at build time and follow i18n SEO best practices
    links.push({ rel: 'canonical', href: canonicalUrl.value })
    
    // Add alternate language links (hreflang) using custom implementation
    // This avoids warnings from useLocaleHead during prerender while still generating correct hreflang links
    const alternateLinks = buildAlternateLinks(
      currentPath.value,
      currentLocale.value,
      supportedLocales,
      defaultLocale
    )
    links.push(...alternateLinks)

    return links
  })

  // Build structured data
  const structuredData = computed(() => {
    const data: Array<Record<string, any>> = []

    // Always include Organization
    data.push(buildOrganizationSchema())

    // Include WebSite schema for homepage
    if (currentPath.value === '/' || currentPath.value === `/${currentLocale.value}`) {
      data.push(buildWebSiteSchema())
    }

    // Include BreadcrumbList for non-homepage pages
    if (currentPath.value !== '/' && currentPath.value !== `/${currentLocale.value}`) {
      data.push(buildBreadcrumbSchema(currentPath.value, currentLocale.value, defaultLocale))
    }

    // Add custom structured data if provided
    if (options.structuredData) {
      if (Array.isArray(options.structuredData)) {
        data.push(...options.structuredData)
      } else {
        data.push(options.structuredData)
      }
    }

    return data
  })

  // Get html lang attribute from locale ISO code
  const htmlLang = computed(() => {
    const currentLocaleCode = currentLocale.value
    return localeIsoMap.value[currentLocaleCode] || 'en-US'
  })

  // Apply SEO to head - manually set lang attribute and use our custom SEO
  useHead({
    // Set html lang attribute based on current locale's ISO code
    htmlAttrs: {
      lang: htmlLang.value
    },
    title: options.title,
    meta: metaTags.value,
    link: linkTags.value,
    script: structuredData.value.map((data, index) => ({
      type: 'application/ld+json',
      children: JSON.stringify(data),
      key: `structured-data-${index}`
    }))
  })
}

